import React, { 
  useState, 
  useEffect, 
  ReactNode, 
  cloneElement, 
  isValidElement 
} from 'react';
import { 
  useFloating, 
  autoUpdate, 
  flip, 
  shift, 
  offset, 
  size,
  useDismiss,
  useInteractions,
  useClick,
  useRole,
  FloatingFocusManager,
  FloatingPortal
} from '@floating-ui/react';

interface BaseDropdownProps {
  children: ReactNode;
  trigger: ReactNode;
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
  placement?: 'bottom-start' | 'top-start' | 'bottom-end' | 'top-end';
  className?: string;
  contentClassName?: string;
  width?: 'trigger' | 'auto' | number;
  maxHeight?: number;
  offset?: number;
  id?: string;
}

export const BaseDropdown: React.FC<BaseDropdownProps> = ({
  children,
  trigger,
  isOpen: controlledOpen,
  onToggle,
  placement = 'bottom-start',
  className = '',
  contentClassName = '',
  width = 'trigger',
  maxHeight = 400,
  offset: offsetValue = 4,
  id
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  const handleToggle = (newOpen: boolean) => {
    if (isControlled) {
      onToggle?.(newOpen);
    } else {
      setInternalOpen(newOpen);
    }
  };

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: handleToggle,
    placement,
    middleware: [
      offset(offsetValue),
      flip({
        fallbackPlacements: ['top-start', 'bottom-start', 'top-end', 'bottom-end']
      }),
      shift({ padding: 8 }),
      size({
        apply({ availableHeight, elements }) {
          const maxHeightValue = Math.min(maxHeight, availableHeight - 16);
          Object.assign(elements.floating.style, {
            maxHeight: `${maxHeightValue}px`,
            overflow: 'hidden',
          });
        },
        padding: 8,
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'listbox' });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  // Apply width styling
  useEffect(() => {
    if (refs.floating.current && isOpen) {
      const floating = refs.floating.current;
      
      if (width === 'trigger' && refs.domReference.current) {
        const triggerWidth = refs.domReference.current.getBoundingClientRect().width;
        floating.style.width = `${Math.max(triggerWidth, 200)}px`;
      } else if (width === 'auto') {
        floating.style.width = 'auto';
        floating.style.minWidth = '200px';
      } else if (typeof width === 'number') {
        floating.style.width = `${width}px`;
      }
    }
  }, [isOpen, width, refs.floating, refs.domReference]);

  // Clone trigger element and add ref and props
  const triggerElement = isValidElement(trigger) 
    ? cloneElement(trigger as React.ReactElement<Record<string, unknown>>, {
        ...getReferenceProps(),
        ref: refs.setReference,
        'aria-expanded': isOpen,
        'aria-haspopup': 'listbox',
        id: id,
      })
    : trigger;

  return (
    <div className={`base-dropdown ${className}`}>
      {triggerElement}
      
      {isOpen && (
        <FloatingPortal>
          <FloatingFocusManager context={context}>
            <div
              ref={refs.setFloating}
              style={{
                ...floatingStyles,
                zIndex: 9999,
              }}
              className={`base-dropdown-content ${contentClassName}`}
              {...getFloatingProps()}
            >
              {children}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </div>
  );
};
